"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface DataTableProps {
  title?: string;
  description?: string;
  headers: string[];
  rows: string[][];
}

const DataTable = ({ title, description, headers, rows }: DataTableProps) => {
  if (!headers || !rows || rows.length === 0) return null;

  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-5xl mx-auto">
        {/* Header */}
        {(title || description) && (
          <div className="text-center mb-12 space-y-4">
            {title && (
              <BlurFade delay={0.1}>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                  {title}
                </h2>
              </BlurFade>
            )}
            {description && (
              <BlurFade delay={0.15}>
                <p className="text-foreground/60 text-lg max-w-2xl mx-auto">
                  {description}
                </p>
              </BlurFade>
            )}
          </div>
        )}

        {/* Table */}
        <BlurFade delay={0.2}>
          <div className="rounded-3xl border border-foreground/5 overflow-hidden bg-muted/20">
            <Table>
              <TableHeader>
                <TableRow className="border-foreground/10 bg-muted/50 hover:bg-muted/50">
                  {headers.map((header, i) => (
                    <TableHead
                      key={i}
                      className="text-foreground font-bold text-sm uppercase tracking-wider py-4"
                    >
                      {header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    className={`border-foreground/5 ${
                      rowIndex % 2 === 0 ? "bg-white" : "bg-muted/20"
                    } hover:bg-muted/40`}
                  >
                    {row.map((cell, cellIndex) => (
                      <TableCell
                        key={cellIndex}
                        className={`py-4 text-foreground/70 ${
                          cellIndex === 0 ? "font-medium text-foreground" : ""
                        }`}
                      >
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </BlurFade>
      </div>
    </section>
  );
};

export default DataTable;
